import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Plus, Search, Edit, Trash2, Eye, Phone, Mail, MapPin, Calendar, Briefcase, Users, BarChart2, KeyRound } from "lucide-react";

const Avatar = ({ name, photo, size = "md" }) => {
  const sz = size === "sm" ? "h-8 w-8 text-xs" : "h-10 w-10 text-sm";
  if (photo) return <img src={photo} alt={name} className={`${sz} rounded-full object-cover`} />;
  const initials = name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "??";
  return (
    <div className={`${sz} rounded-full bg-[#38b6e8]/20 flex items-center justify-center shrink-0`}>
      <span className="text-[#38b6e8] font-bold">{initials}</span>
    </div>
  );
};

export default function EmployeeDirectory() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [viewEmp, setViewEmp] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [resetEmpId, setResetEmpId] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  const fetchEmployees = () => {
    api.get("/employees").then((r) => setEmployees(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchEmployees(); }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/employees/${deleteId}`);
      toast.success("Employee deleted");
      setDeleteId(null);
      fetchEmployees();
    } catch {
      toast.error("Failed to delete employee");
    }
  };

  const handleResetPassword = async () => {
    if (!newPwd || newPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("Passwords do not match"); return; }
    setResetLoading(true);
    try {
      await api.put(`/employees/${resetEmpId}/reset-password`, { new_password: newPwd });
      toast.success("Password reset successfully");
      setResetEmpId(null); setNewPwd(""); setConfirmPwd("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetLoading(false);
    }
  };

  const handleView = async (id) => {
    setViewLoading(true);
    setViewEmp({});
    try {
      const r = await api.get(`/employees/${id}`);
      setViewEmp(r.data);
    } catch {
      toast.error("Failed to load employee");
      setViewEmp(null);
    } finally {
      setViewLoading(false);
    }
  };

  const filtered = employees.filter(
    (e) =>
      `${e.first_name} ${e.last_name} ${e.email} ${e.job_role}`
        .toLowerCase()
        .includes(search.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="flex items-center justify-between mb-7">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Employee Directory</h1>
            <p className="text-slate-500 text-sm mt-1">{employees.length} employees registered</p>
          </div>
          <Button
            onClick={() => navigate("/admin/employees/new")}
            className="bg-[#1a2332] hover:bg-[#2c3e50] text-white gap-2"
            data-testid="add-employee-btn"
          >
            <Plus size={16} /> Add Employee
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-5 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search employees..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 border-slate-200 focus-visible:ring-[#38b6e8]"
            data-testid="employee-search-input"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Users size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No employees found</p>
              <p className="text-slate-400 text-sm">Add your first employee to get started</p>
            </div>
          ) : (
            <table className="w-full" data-testid="employees-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Employee", "Job Role", "Email", "Phone", "Joining Date", "Actions"].map((h) => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((emp) => (
                  <tr key={emp.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`employee-row-${emp.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={`${emp.first_name} ${emp.last_name}`} />
                        <div>
                          <p className="font-semibold text-slate-800 text-sm">{emp.first_name} {emp.last_name}</p>
                          <p className="text-slate-400 text-xs">{emp.id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <Badge variant="secondary" className="bg-[#38b6e8]/10 text-[#0c4a6e] hover:bg-[#38b6e8]/15 font-medium text-xs">
                        {emp.job_role}
                      </Badge>
                    </td>
                    <td className="px-5 py-4 text-slate-600 text-sm">{emp.email}</td>
                    <td className="px-5 py-4 text-slate-600 text-sm">{emp.phone}</td>
                    <td className="px-5 py-4 text-slate-600 text-sm">{emp.joining_date}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleView(emp.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-[#38b6e8] hover:bg-sky-50 transition-colors"
                          title="View Profile"
                          data-testid={`view-emp-${emp.id}`}
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/employees/${emp.id}/report`)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                          title="View Report"
                          data-testid={`report-emp-${emp.id}`}
                        >
                          <BarChart2 size={15} />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/employees/${emp.id}/edit`)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-[#1a2332] hover:bg-slate-100 transition-colors"
                          title="Edit"
                          data-testid={`edit-emp-${emp.id}`}
                        >
                          <Edit size={15} />
                        </button>
                        <button
                          onClick={() => { setResetEmpId(emp.id); setNewPwd(""); setConfirmPwd(""); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          title="Reset Password"
                          data-testid={`reset-pwd-${emp.id}`}
                        >
                          <KeyRound size={15} />
                        </button>
                        <button
                          onClick={() => setDeleteId(emp.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete"
                          data-testid={`delete-emp-${emp.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetEmpId} onOpenChange={() => { setResetEmpId(null); setNewPwd(""); setConfirmPwd(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Employee Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium text-slate-700">New Password</Label>
              <Input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Min. 6 characters"
                className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8]"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Confirm Password</Label>
              <Input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Re-enter new password"
                className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8]"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-5">
            <Button variant="outline" onClick={() => setResetEmpId(null)} className="border-slate-200">Cancel</Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetLoading}
              className="bg-[#1a2332] hover:bg-[#2c3e50] text-white"
              data-testid="confirm-reset-password-btn"
            >
              {resetLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the employee and their login account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Dialog */}
      <Dialog open={!!viewEmp} onOpenChange={() => setViewEmp(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Employee Profile</DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="py-8 text-center text-slate-400">Loading...</div>
          ) : viewEmp && viewEmp.id ? (
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <Avatar name={`${viewEmp.first_name} ${viewEmp.last_name}`} photo={viewEmp.photo} size="lg" />
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">{viewEmp.first_name} {viewEmp.last_name}</h3>
                  <Badge className="bg-[#38b6e8]/10 text-[#0c4a6e] text-xs mt-1">{viewEmp.job_role}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {[
                  { icon: Mail, label: "Email", val: viewEmp.email },
                  { icon: Phone, label: "Phone", val: viewEmp.phone },
                  { icon: Calendar, label: "Joining Date", val: viewEmp.joining_date },
                  { icon: Briefcase, label: "Aadhar No", val: viewEmp.aadhar_no },
                  { icon: Briefcase, label: "PAN No", val: viewEmp.pan_no },
                  { icon: MapPin, label: "Address", val: viewEmp.address },
                ].map(({ icon: Icon, label, val }) => (
                  <div key={label} className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                    <Icon size={14} className="text-[#38b6e8] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-slate-400 text-xs">{label}</p>
                      <p className="text-slate-800 font-medium text-xs mt-0.5 break-all">{val}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
